import { cn } from '@sandtech-hub-monorepo/ui-kit';

import { AvailableProgram, programStats } from '../../../data/programs';
import { DetailStat } from './DetailStat';

interface Props {
  program: AvailableProgram;
  className?: string;
}

/**
 * The hero strip: how big the program is, how much of it is free, and what
 * the learner ends up with.
 *
 * Every cell is marketing copy off the program itself (PEDS-17090 added
 * `coursesSummary`, `modulesSummary` and `certificateSummary`; the time
 * commitment reuses the `marketingCommitment` column that already existed).
 * It costs no request - `GetViewerAvailablePrograms` already resolved this
 * page - so the strip renders with the hero rather than after a round trip.
 *
 * It used to count Savanna's activities instead. That could only see the one
 * curriculum the page fetched, so every figure carried an "in Course 1"
 * qualifier under a strip that reads as program-wide, and it made the hero
 * wait on a Savanna call. The counts are still shown where they are true:
 * per course, on the accordion headers below.
 *
 * Renders nothing when no cell has copy, rather than an empty rule under the
 * title.
 */
export const DetailStatsStrip = ({ program, className }: Props) => {
  const stats = programStats(program);

  if (stats.length === 0) {
    return null;
  }

  return (
    <div
      className={cn(
        // Figma's mobile frame stacks this as a 2x2 grid with a horizontal
        // rule between the two rows; from `sm:` up it's a single row of 4
        // with only vertical dividers between cells. `nth-child(n+3)`
        // targets "row 2 onward" regardless of how many stats actually
        // render (3 stats still puts the divider correctly above the lone
        // second-row cell; fewer than 3 never adds one).
        'grid grid-cols-2 gap-y-4 [&>*:nth-child(n+3)]:border-t [&>*:nth-child(n+3)]:border-white [&>*:nth-child(n+3)]:pt-4 sm:flex sm:flex-nowrap sm:divide-x sm:divide-white sm:[&>*:nth-child(n+3)]:border-t-0 sm:[&>*:nth-child(n+3)]:pt-0',
        className
      )}
    >
      {stats.map((stat) => (
        <DetailStat key={stat.label} label={stat.label} value={stat.value} />
      ))}
    </div>
  );
};
